import React, { createRef, Suspense, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  BypassBlock,
  classNames,
  ToastMode,
  ToastProvider,
  useDefineMessages,
  useToasts,
  TooltipProvider,
} from '@groma-dev/blueprint';
import { initializeAuthInterceptor, SiteExp } from '@groma-dev/scaffold';
import { Route, Routes, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'js-cookie';
import { AppConfig } from '../../../core/AppConfig';
import { jumpToAnchorRef, scrollToTop } from '../../utils/WebHelper';
import { useAppStore, useDataStore } from '../../providers/StoresProvider';
import { ComponentFactory } from '../../ComponentFactory';
import { AppRoutes } from '../../../core/routes';
import { APIClient } from '../../../core/apiclient/APIClient';
import AuthRouter from '../authrouter/AuthRouter';
import { AppMode } from '../../stores/AppStoreInterface';
import { handleErrorLogging } from '../../../core/utils/CoreHelper';
import { LocalStorage } from '../../../core/utils/Constants';
import { getAppStyles } from './styles/App.styles';

const AccountSelect = React.lazy(() => ComponentFactory.AccountSelectAsync);
const Documents = React.lazy(() => ComponentFactory.DocumentsAsync);
const ErrorPage = React.lazy(() => ComponentFactory.ErrorPageAsync);
const Help = React.lazy(() => ComponentFactory.HelpAsync);
const Home = React.lazy(() => ComponentFactory.HomeAsync);
const Invest = React.lazy(() => ComponentFactory.InvestAsync);
const Overview = React.lazy(() => ComponentFactory.OverviewAsync);
const SideNav = React.lazy(() => ComponentFactory.SideNavAsync);
const Wallet = React.lazy(() => ComponentFactory.WalletAsync);

let authRedirectInitialized = false;

export interface AppProps {}

const AppContent = observer(() => {
  const dataStore = useDataStore();
  const appStore = useAppStore();
  const messages = useDefineMessages();
  const { mode } = appStore.state;
  const { formatMessage } = useIntl();
  const styles = getAppStyles();
  const { addToast } = useToasts();
  const { userState, accountsState } = dataStore;
  const { pathname } = useLocation();

  const fetchAccount = async (id: string) => {
    try {
      const response = await dataStore.fetchAccount({ id });

      if (response.success) {
        dataStore.setAccount(response.account);
      } else {
        setTimeout(
          () => addToast(response.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR),
          100
        );
      }
    } catch (e) {
      handleErrorLogging(e as Error);
    }
  };

  const fetchAccounts = async (page = 1) => {
    const { pageSize } = AppConfig.Components.App.accounts;
    try {
      const response = await dataStore.fetchAccounts({ page_size: pageSize, page });

      if (response.success) {
        dataStore.setAccounts([...(dataStore.accountsState.accounts ?? []), ...response.accounts]);
        // if there is a nextPage, recursively fetch accounts
        if (response.nextPageUrl) {
          await fetchAccounts(page + 1);
          // if there are no more account pages to fetch, check to see if stored account is in accounts
        } else {
          const { accounts = [] } = dataStore.accountsState;

          if (accounts?.length > 0) {
            const lsInvestmentAccount = dataStore.getLocalStorage(LocalStorage.InvestmentAccount);
            const validAccountStored =
              lsInvestmentAccount && accounts.some(acc => acc.id === parseInt(lsInvestmentAccount));
            // determine if local storage id exists in user's accessible accounts
            // otherwise, use first account in stored accounts
            await fetchAccount(validAccountStored ? lsInvestmentAccount : `${accounts[0].id}`);
          }

          dataStore.setFetchingAccounts(false);
          dataStore.setAppInitialized(true);
        }
      } else if (response.status === 404) {
        // handle 403 response which is resolves to no results
        dataStore.setAccounts([]);
        dataStore.setAppInitialized(true);
      } else {
        setTimeout(
          () => addToast(response.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR),
          100
        );
      }
    } catch (e) {
      handleErrorLogging(e as Error);
    }
  };

  useEffect(() => {
    if (userState.user) {
      if (!accountsState.isFetching && !accountsState.accounts) {
        fetchAccounts();
      }
    }
  }, [userState.user]);

  if (!dataStore.appState.initialized) return <ComponentFactory.Loading isPage />;

  return (
    <>
      <ComponentFactory.Header />
      <div id="mainContent" className={styles.mainContent}>
        <ComponentFactory.ErrorBoundary>
          <Suspense>
            {mode === AppMode.Desktop && pathname !== AppRoutes.root && <SideNav />}
            {mode !== AppMode.Desktop && pathname !== AppRoutes.root && (
              <div className={classNames(styles.mobileAccountSelectContainer)}>
                <AccountSelect />
              </div>
            )}
            <Routes>
              <Route path={AppRoutes.root} element={<Home />} />
              <Route path={AppRoutes.Overview} element={<Overview />} />
              <Route path={AppRoutes.Wallet} element={<Wallet />} />
              <Route path={AppRoutes.Documents} element={<Documents />} />
              <Route path={AppRoutes.Invest} element={<Invest />} />
              <Route path={AppRoutes.Help} element={<Help />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </Suspense>
        </ComponentFactory.ErrorBoundary>
      </div>
      <ComponentFactory.Footer />
    </>
  );
});

const App = observer(() => {
  const styles = getAppStyles();
  const { pathname, search, hash } = useLocation();
  const { Domains, Authentication } = AppConfig.Settings;
  const { name: authCookieName, domain } = Authentication.cookie;
  const resetRef = createRef<HTMLSpanElement>();

  if (Authentication.enabled && Cookies.get(authCookieName) && !authRedirectInitialized) {
    const returnUrl = encodeURIComponent(`/${search}`);
    initializeAuthInterceptor(
      APIClient,
      `${Domains.id.baseUrl}/signin?return_url=${returnUrl}&site_exp=${SiteExp.INVESTORS}`,
      {
        name: authCookieName,
        domain,
      }
    );
    authRedirectInitialized = true;
  }

  useEffect(() => {
    if (hash) jumpToAnchorRef();
    else {
      scrollToTop();
      // Move focus to top of the page if no hash in url
      if (!hash) resetRef.current?.focus();
    }
  }, [pathname]);

  return (
    <>
      <span ref={resetRef} tabIndex={-1} />
      <BypassBlock id="mainContent">
        <FormattedMessage
          id="common.bypassBlocks.mainContent"
          defaultMessage="Skip to Main Content"
          description="Main bypass block content"
        />
      </BypassBlock>
      <ToastProvider>
        <div className={classNames(styles.mainContainer)}>
          <AuthRouter>
            <ComponentFactory.ErrorBoundary>
              <TooltipProvider>
                <Routes>
                  <Route path={AppRoutes.Auth} element={<ComponentFactory.Auth />} />
                  <Route path="*" element={<AppContent />} />
                </Routes>
              </TooltipProvider>
            </ComponentFactory.ErrorBoundary>
          </AuthRouter>
        </div>
      </ToastProvider>
    </>
  );
});

export default App;
